import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { UiModalComponent } from '@components';
import {
    IFolder,
    IFolderAssignment,
    IUser,
    ModalResultType,
    RolesAssignment,
    UserType,
} from '@models';
import { UserGroupField } from '@pages/users/modals/user-group-import';
import {
    FoldersService,
    RoleService,
    UserService,
} from '@services';
import { addToObjectIf } from '@utils/object.utils';

import {
    BehaviorSubject,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    filter,
    finalize,
    map,
    startWith,
    switchMap,
    take,
} from 'rxjs/operators';

export interface IAssignUserModalData {
    folder: IFolder;
    assignment?: IFolderAssignment;
}

@Component({
    templateUrl: './assign-user.modal.html',
    styleUrls: ['./assign-user.modal.scss'],
    providers: [
        RoleService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class AssignUserModalComponent
    extends UiModalComponent<IAssignUserModalData>
    implements OnInit, OnDestroy {

    public readonly UserType = UserType;
    public readonly UserGroupField = UserGroupField;

    public form!: FormGroup;
    public loading$ = new BehaviorSubject<boolean>(false);
    public loadingRoles$ = new BehaviorSubject<boolean>(false);
    public isInheritingRolesFromTenant = this.context.data.folder.rolesAssignment === RolesAssignment.InheritFromTenant;
    public selectedUser$!: Observable<Pick<IUser, 'type' | 'rolesList'> | null>;
    public isLocalUser = !!this.context.additionalData.isLocalUser;

    @HostBinding('class')
    public hostClass = 'assign-user-modal';

    private _fb = this._injector.get<FormBuilder>(FormBuilder);
    private _foldersService = this._injector.get<FoldersService>(FoldersService);
    private _userService = this._injector.get<UserService>(UserService);
    private _roleService = this._injector.get<RoleService>(RoleService);
    private _selectUser$ = new BehaviorSubject<undefined | number | { domain: string, username: string }>(undefined);

    private _destroyed$ = new Subject();

    ngOnInit() {
        this.form = this._fb.group({
            [UserGroupField.Domain]: { disabled: this.isLocalUser, value: [] },
            [UserGroupField.UserName]: [{
                disabled: this.isLocalUser,
                value: [],
            }, {
                asyncValidators: [this._userAlreadyAssignedValidator],
            }],
            [UserGroupField.Type]: { disabled: this.isLocalUser, value: [] },
            user: [{
                disabled: !this.isLocalUser,
                value: !!this.context.data.assignment
                    ? [{
                        id: this.context.data.assignment.user.id,
                        text: this.context.data.assignment.user.userName,
                    }]
                    : [],
            }, {
                validators: [Validators.required],
                asyncValidators: [this._userAlreadyAssignedValidator],
            }],
            ...addToObjectIf<any>(
                !this.isInheritingRolesFromTenant,
                {
                    rolesList: [
                        !!this.context.data.assignment
                            ? this.context.data.assignment.roles
                                .map(role => ({
                                    id: role.id,
                                    text: role.name,
                                }))
                            : [],
                        Validators.required,
                    ],
                },
            ),
        });

        if (!!this.context.data.assignment) {
            this.onUserSelected(this.context.data.assignment.user.id);
        }

        this.selectedUser$ = this._selectUser$.pipe(
            switchMap(user => {
                if (!user) {
                    this.loadingRoles$.next(false);
                    return of(null);
                }

                this.loadingRoles$.next(true);

                // we need to emit immediately for Edit
                const status$ = !!this.context.data.assignment
                    ? this.form.statusChanges.pipe(startWith(this.form.status))
                    : this.form.statusChanges;

                return status$
                    .pipe(
                        filter(status => status !== 'PENDING'),
                        switchMap(() => typeof user === 'number'
                            ? this._userService.getInstance(user)
                            : this._foldersService
                                .getAllRolesForUser({ username: `${user.username}@${user.domain}`, skip: 0, take: 0 })
                                .pipe(
                                    map(roles => ({
                                        type: this.form.get(UserGroupField.Type)!.value,
                                        rolesList: roles.tenantRoles.map(role => role.name),
                                    })),
                                ),
                        ),
                        finalize(() => this.loadingRoles$.next(false)),
                        take(1),
                    );
            }),
        );
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public userSearchFactory = (searchTerm?: string, fetchSize = 20, skip: number = 0) =>
        this._userService.suggest(searchTerm, fetchSize, skip, UserType.User)

    public roleSearchFactory = (searchTerm?: string, fetchSize = 20, skip: number = 0) =>
        this._roleService.suggest(searchTerm, fetchSize, skip, { useId: true })

    public onUserSelected(user?: IUser['id'] | { domain: string, username: string }) {
        this._selectUser$.next(user);
    }

    public switchToEdit() {
        const userControls = [this.form.get('user')!, this.form.get(UserGroupField.UserName)!];
        this.isLocalUser = true;

        // retrieve the already stored user data
        const assignment = userControls.map(value => value.getError('alreadyAssigned')).find(value => !!value) as IFolderAssignment;

        // update the form and modal to edit state
        this.context.data.assignment = assignment;

        userControls[0].enable();
        userControls[0].patchValue([{
            id: assignment.user.id,
            text: assignment.user.userName,
        }], { onlySelf: true, emitEvent: false });

        this.form.get('rolesList')!.setValue(
            (assignment.roles || [])
                .map(role => ({
                    id: role.id,
                    text: role.name,
                })),
        );

        userControls.forEach(control => control.updateValueAndValidity({ emitEvent: false }));
    }

    public onSubmit() {
        if (
            !this.form.valid ||
            this.loading$.value
        ) {
            return;
        }

        this.loading$.next(true);

        const folderId = this.context.data.folder.id;
        let roleIds;

        if (!this.isInheritingRolesFromTenant) {
            roleIds = this.form.value.rolesList
                .map((role: any) => role.id);
        }

        if (this.isLocalUser) {
            const userId = this.form.value.user[0].id;
            this._foldersService
                .addUserToFolder(userId, folderId, roleIds)
                .subscribe(
                    () => this._dialogRef.close({
                        type: ModalResultType.Add,
                    }),
                    () => this.loading$.next(false),
                );
        } else {
            const userPayload = {
                Domain: this.form.get(UserGroupField.Domain)!.value[0].id,
                UserName: this.form.get(UserGroupField.UserName)!.value[0].id,
                UserType: this.form.get(UserGroupField.Type)!.value,
            };

            this._foldersService
                .addAdEntityToFolder(userPayload, folderId, roleIds)
                .subscribe(
                    () => this._dialogRef.close({
                        type: ModalResultType.Add,
                    }),
                    () => this.loading$.next(false),
                );
        }
    }

    private _userAlreadyAssignedValidator = (control: AbstractControl) => {
        // don't validate on edit
        if (this.context.data.assignment) { return of(null); }

        if (!control.value[0]) {
            return of(null);
        }

        const form = control.parent;
        const searchText = this.isLocalUser
            ? control.value[0].text
            : `${control.value[0].id}@${form.get(UserGroupField.Domain)!.value[0].id}`;

        return this._foldersService
            .checkAssignedUser(this.context.data.folder.id, searchText)
            .pipe(
                map(assignments => assignments ? { alreadyAssigned: assignments } : null),
            );
    }
}
